import React, { useEffect, useState } from 'react';
import { Text, PhoneInput } from 'library';
import EmailInput from './InputFields/EmailInput';
import PasswordInput from './InputFields/PasswordInput';
import { track } from 'shared/Tracking';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { isMobile } from 'react-device-detect';
import { Dialog, DialogContent, Grid, SvgIcon } from '@material-ui/core';
import { Colors } from '../../../constants';
import CloseIcon from '@material-ui/icons/Close';
import { PrimaryButton as Button } from 'library';
import useProviderSignUp from 'api/ProviderSignUpApi';
import usePublicSessionTracking, { PublicSessionEventType } from '../../shared/PublicSessionTracking';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    route?: string;
    onUserCreated?: (token: string) => void;
}
export const appLink = `https://apps.apple.com/app/apple-store/id1491607600?pt=120819140&ct=InviteFriends&mt=8`;

export function SignUpDialog({ isOpen, onClose, route, onUserCreated }: IProps) {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [display, setDisplay] = useState<string>('');
    const [isEmail, setIsEmail] = useState<boolean>(true);
    const [isPassword, setIsPassword] = useState<boolean>(true);
    const [isValidPhone, setIsValidPhone] = useState<boolean>(false);
    const [phoneError, setPhoneError] = useState<boolean>(false);
    const [isError, setError] = useState<boolean>(false);
    const [userCreated, setUserCreated] = useState<boolean>(false);
    const completeSignUp = useProviderSignUp();
    const { trackSession } = usePublicSessionTracking('Partial Signup Dialog');

    function getPhoneErrorText() {
        const errorText = phoneError ? 'Please enter a valid phone number' : '';
        return errorText;
    }

    useEffect(() => {
        setIsValidPhone(isValidPhoneNumber(phoneNumber, 'US'));
        const error = !isValidPhone && !!phoneNumber;
        setPhoneError(error);
    }, [phoneNumber, isValidPhone]);

    const submitEnabled = !loading && isValidPhone && isEmail && isPassword;

    function closeModalWithTracking() {
        trackSession('DIALOG_CLOSE', 'Sign up dialog dismissed', PublicSessionEventType.User);
        onClose();
    }

    function content() {
        return (
            <Grid container style={styles.innerContainer}>
                <Grid container>
                    <Grid item xs={12}>
                        <PhoneInput
                            label="Phone"
                            onChange={(value: string, displayPhone: string, isValid: boolean) => {
                                setPhoneNumber(value);
                                setDisplay(displayPhone);
                            }}
                            value={display}
                            error={phoneError}
                            errorText={getPhoneErrorText()}
                        />
                    </Grid>
                    <Grid item xs={12} style={{ paddingTop: 10, paddingBottom: 10 }}>
                        <EmailInput
                            error={!isEmail}
                            errorText={'Please enter your email'}
                            label="Email"
                            value={email}
                            onChange={(value: string, isValid: boolean) => {
                                setIsEmail(isValid);
                                setEmail(value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <PasswordInput
                            error={!isPassword}
                            errorText={
                                'Password must be minimum 8 characters with at least one uppercase letter, one lowercase letter, one digit, and one special character.'
                            }
                            value={password}
                            label="Password"
                            onChange={(value: string, isValid: boolean) => {
                                setPassword(value);
                                setIsPassword(isValid);
                            }}
                        />
                    </Grid>

                    {isError ? (
                        <Grid container direction="row" xs={12} style={{ marginBottom: -20 }}>
                            <Text variant="deprecatedCaption" color={Colors.error}>
                                Invalid username/password combination. If you have a Tandem account, download or open
                                the app.
                            </Text>
                        </Grid>
                    ) : null}
                </Grid>

                <Button
                    loading={loading}
                    onClick={submitSignup}
                    disabled={!submitEnabled}
                    buttonStyle={{ marginTop: 20, maxWidth: isMobile ? '100%' : '50%' }}
                >
                    Let's go!
                </Button>
            </Grid>
        );
    }
    function headerContent() {
        return (
            <Grid container direction="row" style={{ marginBottom: 30 }}>
                <Grid item xs={12}>
                    <Text variant="display" textStyle={{ fontSize: 28 }}>
                        Earn up to
                        <Text
                            bold
                            variant="display"
                            textStyle={{ fontSize: 28, fontWeight: 'bold', color: Colors.turquoise }}
                        >
                            {' '}
                            $4000/month.{' '}
                        </Text>
                    </Text>
                </Grid>
                <Grid item xs={12}>
                    <Text variant="body1">
                        It's time you regain autonomy over your hours and pay. Keep 100% of your earnings on Tandem.
                    </Text>
                </Grid>
            </Grid>
        );
    }

    const submitSignup = async () => {
        if (!isEmail || !isPassword) return;

        setLoading(true);
        try {
            await completeSignUp({
                email: email.toLocaleLowerCase(),
                phoneNumber,
                password,
                shareSource: 'PublicJobsPopup',
                signupRoute: route,
                signupSource: 'FinishSignUpDialog',
                onUserCreated: (token) => {
                    setUserCreated(true);
                    onUserCreated && onUserCreated(token);
                },
            });
        } catch (error) {
            setError(true);
        }
        setLoading(false);
    };

    function getAfterSignup() {
        return (
            <DialogContent style={styles.card}>
                <Grid container direction="row" style={{ marginBottom: 10 }}>
                    <Grid item xs={8}>
                        <Text variant="display" textStyle={{ fontSize: 28 }}>
                            Finish Signing up for Free.
                        </Text>
                    </Grid>
                    <Grid item xs={12}>
                        <Text variant="body1">
                            Head to the app store and download the Tandem app to start earning and see what else is
                            available!
                        </Text>
                    </Grid>
                </Grid>
                <Button
                    onClick={async () => {
                        track('Provider opened app store from public job page');
                        trackSession('NAVIGATE_FROM', 'To App Store - Button', PublicSessionEventType.User, {
                            link: appLink,
                        });
                        window.open(appLink, '_blank');
                    }}
                    disabled={false}
                    buttonStyle={{ marginTop: 20, maxWidth: isMobile ? '100%' : '50%' }}
                >
                    Download Tandem
                </Button>
            </DialogContent>
        );
    }

    return (
        <Dialog
            open={isOpen}
            PaperProps={{
                style: {
                    borderRadius: 20,
                    background: `linear-gradient(to right, ${Colors.mediumTurq}, ${Colors.lightGreen})`,
                },
            }}
        >
            <Grid container direction="row" justify="flex-end">
                <Grid item xs={1}>
                    <div
                        style={{
                            cursor: 'pointer',
                            paddingTop: 12,
                        }}
                        onClick={closeModalWithTracking}
                    >
                        <SvgIcon
                            component={CloseIcon}
                            viewBox="0 0 26 26"
                            style={{
                                color: Colors.darkNavy,
                            }}
                        />
                    </div>
                </Grid>
            </Grid>
            {!userCreated ? (
                <DialogContent style={styles.card}>
                    {headerContent()}
                    {content()}
                </DialogContent>
            ) : (
                getAfterSignup()
            )}
        </Dialog>
    );
}

const styles = {
    card: {
        paddingTop: '10px',
        paddingBottom: '64px',
        paddingLeft: '24px',
        paddingRight: '24px',
    },
    innerContainer: {
        padding: 25,
        backgroundColor: Colors.white,
        borderRadius: 20,
    },
};
